
import React from 'react'
import getProp from '@f/get-prop'
import moment from 'moment-timezone'
import { hasPastDuePayment, isBusinessTier, isGrandfathered, isWorkspaceInvoicedAndWithQuota } from 'lib/utils/billing'
import humanize from 'humanize-number'

/**
 * Banner checks.
 * Order matters here. Only one banner should be viewed at a time.
 * Define the most critical banner first.
 */

const checks = [
  function checkPastDueCreditCard ({ workspaceSlug, billing }) {
    // Skip legacy plans
    if (isGrandfathered({ billing })) return

    const isPastDue = hasPastDuePayment({ billing })

    if (isPastDue) {
      return {
        importance: 'critical',
        violation: 'bad cc',
        message: <span><strong>We had trouble charging your credit card.</strong> Please update your billing information by {moment().endOf('month').format('MMMM Do')} to continue using Segment.</span>,
        cta: 'Update Billing Info',
        buttonPath: `/${workspaceSlug}/settings/billing/card`
      }
    }
  },

  function checkOverMTULimit ({ workspaceSlug, billing, billingCount }) {
    const anonymousCountsThisMonth = getProp('thisMonth.anonymous', billingCount) || 0
    const userCountsThisMonth = getProp('thisMonth.users', billingCount) || 0
    const totalMTUsThisMonth = (anonymousCountsThisMonth + userCountsThisMonth) || 0
    const anonymousCountsLastMonth = getProp('lastMonth.anonymous', billingCount) || 0
    const userCountsLastMonth = getProp('lastMonth.users', billingCount) || 0
    const totalMTUsLastMonth = (anonymousCountsLastMonth + userCountsLastMonth) || 0
    const trackedObjects = getProp('quota.trackedObjects', billing) || 0
    const humanizeTrackedObjects = humanize(trackedObjects)
    const isDeveloperPlan = getProp('subscription.plan.id', billing) === 'developer'

    // MTU upgrade banners can only show for invoiced workspace w/ a monthly quota
    const isInvoicedWorkspaceWithQuota = isWorkspaceInvoicedAndWithQuota(billing)

    const isOverMTUsQuotaThisMonth = totalMTUsThisMonth > trackedObjects
    const isOverMTUsQuotaLastMonth = totalMTUsLastMonth > trackedObjects

    if (!isOverMTUsQuotaThisMonth && !isOverMTUsQuotaLastMonth) return
    if (!isInvoicedWorkspaceWithQuota && !isDeveloperPlan) return

    const bannerContent = {
      importance: 'critical',
      violation: 'mtus'
    }
    const mtuCount = isOverMTUsQuotaThisMonth ? humanize(totalMTUsThisMonth) : humanize(totalMTUsLastMonth)
    const lastMonth = moment().subtract(1, 'month').format('MMMM')
    //  FIXME (@benjaminhoffman): once we remove workspace-settings legacy UI, the URL below should always direct to /change-plan
    const url = isGrandfathered({ billing }) ? `/${workspaceSlug}/settings/billing/plan` : `/${workspaceSlug}/settings/change-plan`

    const copy = isOverMTUsQuotaThisMonth
      ? <span>You used {mtuCount} MTUs so far this month</span>
      : <span>You used {mtuCount} MTUs in {lastMonth}</span>

    if (isInvoicedWorkspaceWithQuota) {
      bannerContent.message = <span><strong>Your workspace has a limit of {humanizeTrackedObjects} MTUs per month</strong>. {copy}. Overage charges may apply.</span>
    } else {
      bannerContent.cta = 'Upgrade Plan'
      bannerContent.buttonPath = url
      bannerContent.message = <span><strong>Your workspace has a limit of {humanizeTrackedObjects} MTUs per month</strong>. {copy}. Please upgrade to our Team Plan.</span>
    }

    return bannerContent
  },

  function checkTrialWithoutCardInfo ({ workspaceSlug, billing }) {
    const isTrial = getProp('subscription.trial', billing)
    const hasCard = billing.card

    if (isTrial && !hasCard) {
      const trialEnd = getProp('subscription.trialEnd', billing)
      const humanizedDays = humanizeTimeUntilTrialEnd(trialEnd)

      return {
        importance: 'important',
        violation: 'trial ending w/o card',
        message: <span><strong>You have {humanizedDays} left on your free trial.</strong> Please enter your billing information to continue using Segment.</span>,
        icon: 'card',
        cta: 'Update Billing Info',
        buttonPath: `/${workspaceSlug}/settings/billing/card`
      }
    }
  },

  function checkSoonToExpireCard ({ workspaceSlug, billing }) {
    if (!billing.card) return
    if (isBusinessTier({ billing })) return

    const { expirationMonth, expirationYear } = billing.card
    if (!expirationMonth || !expirationYear) return

    const expirationDate = moment().month(expirationMonth - 1).year(expirationYear).startOf('month')
    const thresholdDate = moment().add(2, 'months')
    const isExpiringSoon = expirationDate.isBefore(thresholdDate)

    if (isExpiringSoon) {
      return {
        importance: 'important',
        violation: 'cc expiring',
        message: <span><strong>Your credit card will expire soon.</strong> Please update it to continue using Segment without interruption.</span>,
        icon: 'card',
        cta: 'Update Billing Info',
        buttonPath: `/${workspaceSlug}/settings/billing/card`
      }
    }
  },

  function checkTrialWithCardInfo ({ billing }) {
    const isTrial = getProp('subscription.trial', billing)
    const hasCard = billing.card

    if (isTrial && hasCard) {
      const trialEnd = getProp('subscription.trialEnd', billing)
      const humanizedDays = humanizeTimeUntilTrialEnd(trialEnd)

      return {
        importance: 'info',
        violation: 'trial ending w/ card',
        message: <span>You have {humanizedDays} left on your free trial of Segment.</span>,
        icon: 'calendar'
      }
    }
  }
]

function getRemainingDuration (trialEnd) {
  return (new Date(trialEnd)).getTime() - Date.now()
}

function humanizeTimeUntilTrialEnd (trialEnd) {
  const remainingDuration = getRemainingDuration(trialEnd)
  let remainingDays = Math.ceil(moment.duration(remainingDuration, 'ms').asDays())

  if (remainingDays < 0) remainingDays = 0

  return `${remainingDays} ${remainingDays === 1 ? 'day' : 'days'}`
}

/**
 * Exports.
 */

export function findBanner (data) {
  let banner

  for (var i = 0; i < checks.length; i++) {
    banner = checks[i](data)
    if (banner) return banner
  }
}

export function adminModeBanner () {
  return {
    importance: 'info',
    violation: 'is admin mode and not workspace admin',
    message: <span>You are in read-only <strong>admin mode</strong>. Any changes you make to this workspace will not be implemented.</span>
  }
}



// WEBPACK FOOTER //
// ./ui/checks/index.js